import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { KpiRow, KpiStrip, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { Cliente, RediviaCatalogo, RediviaLinea, RediviaNegocio } from '@/lib/types'
import {
  IconAlertTriangle, IconBan, IconBarChart, IconCheck, IconEdit, IconMail, IconPlus,
  IconSearch, IconTrash, IconZap,
} from '@/components/icons/Icons'

const ESTADO_LABEL: Record<string, string> = {
  activo: 'Activo',
  suspendido: 'Suspendido',
  cancelado: 'Cancelado',
}

const ESTADO_BADGE: Record<string, string> = {
  activo: 'badge-aceptada',
  suspendido: 'badge-warning',
  cancelado: 'badge-rechazada',
}

const r2 = (n: number) => Math.round(n * 100) / 100

/* Los tramos de Redivia de menor a mayor. Comparar lo que el cliente PAGA con
   lo que su uso SUGIERE es lo que convierte una tabla en un panel: si el
   sugerido es mayor, el plan le quedó chico. Un tramo desconocido no alerta. */
const ORDEN_TRAMOS = ['demo', 'emprendedor', 'negocio', 'empresa']

function hayUpsell(n: RediviaNegocio): boolean {
  const contratado = ORDEN_TRAMOS.indexOf((n.plan_slug || '').toLowerCase())
  const sugerido = ORDEN_TRAMOS.indexOf((n.uso?.tramo_sugerido || '').toLowerCase())
  return contratado >= 0 && sugerido >= 0 && sugerido > contratado
}

const MODULO_LABEL: Record<string, string> = {
  atencion: 'Atención',
  cobros: 'Cobros',
  senales: 'Señales',
}

interface FormNegocio {
  cliente_id: number | ''
  tenant_slug: string
  plan_slug: string
  plan_nombre: string
  moneda: string
  dia_cobro: number
  lineas: RediviaLinea[]
  notas: string
}

const vacio: FormNegocio = {
  cliente_id: '', tenant_slug: '', plan_slug: '', plan_nombre: '',
  moneda: 'PEN', dia_cobro: 1, lineas: [{ concepto: '', monto: 0 }], notas: '',
}

export default function RediviaNegocios() {
  const api = useApi()
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')

  const [negocios, setNegocios] = useState<RediviaNegocio[]>([])
  const [kpis, setKpis] = useState<{
    activos: number; suspendidos: number; mrr: Record<string, number>; cobros_pendientes: number
    cerca_del_tope: number; con_upsell: number; uso_disponible: boolean
  } | null>(null)
  /** El negocio cuyo detalle operativo se está mirando. */
  const [detalle, setDetalle] = useState<RediviaNegocio | null>(null)
  /** El corte que se está por aplicar, con el motivo que verá el cliente. */
  const [corte, setCorte] = useState<{ n: RediviaNegocio; estado: 'suspendido' | 'cancelado' } | null>(null)
  const [motivoCorte, setMotivoCorte] = useState('')
  const [ocupado, setOcupado] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [catalogo, setCatalogo] = useState<RediviaCatalogo | null>(null)

  const [modal, setModal] = useState<'create' | 'edit' | null>(null)
  const [form, setForm] = useState<FormNegocio>(vacio)
  const [editId, setEditId] = useState<number | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos')

  const cargar = useCallback(async () => {
    try {
      const r = await api.get<{ negocios: RediviaNegocio[]; kpis: any }>('/api/redivia/negocios')
      setNegocios(r.negocios || [])
      setKpis(r.kpis || null)
    } catch (e: any) {
      showToast(e?.message || 'No se pudieron cargar los negocios', 'error')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { void cargar() }, [cargar])

  /* El catálogo vive en Redivia. Si no responde, el alta carga el monto a mano:
     que el catálogo esté afuera no puede impedir cobrar. */
  useEffect(() => {
    api.get<RediviaCatalogo>('/api/redivia/planes')
      .then(setCatalogo)
      .catch(() => setCatalogo({ planes: [], disponible: false, motivo: 'No se pudo consultar Redivia' }))
  }, [api])

  const filtrados = useMemo(() => {
    let r = negocios
    if (filtroEstado !== 'todos') r = r.filter(n => n.estado === filtroEstado)
    if (search) {
      const q = search.toLowerCase()
      r = r.filter(n =>
        n.cliente_nombre?.toLowerCase().includes(q) ||
        n.tenant_slug.toLowerCase().includes(q) ||
        n.plan_nombre?.toLowerCase().includes(q)
      )
    }
    return r
  }, [negocios, filtroEstado, search])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtrados, 15, `${search}|${filtroEstado}`)

  const hayFiltros = !!search || filtroEstado !== 'todos'

  /* El monto mensual es la SUMA de las líneas, no un campo aparte: el backend
     exige que coincidan, así que derivarlo evita el error antes de cometerlo. */
  const montoForm = useMemo(() => r2(form.lineas.reduce((a, l) => a + Number(l.monto || 0), 0)), [form.lineas])

  const abrirAlta = () => { setForm(vacio); setEditId(null); setModal('create') }

  const abrirEdicion = (n: RediviaNegocio) => {
    const lineas = n.lineas?.length ? n.lineas : [{ concepto: n.plan_nombre || 'Membresía', monto: n.monto_mensual }]
    setForm({
      cliente_id: n.cliente_id, tenant_slug: n.tenant_slug,
      plan_slug: n.plan_slug || '', plan_nombre: n.plan_nombre || '',
      moneda: n.moneda, dia_cobro: n.dia_cobro, lineas, notas: n.notas || '',
    })
    setEditId(n.id)
    setModal('edit')
  }

  /* Elegir un plan del catálogo reemplaza las líneas por las suyas: el plan base
     y sus complementos (los tramos del canal de WhatsApp). */
  const aplicarPlan = (slug: string) => {
    const plan = catalogo?.planes.find(p => p.slug === slug)
    if (!plan) { setForm(f => ({ ...f, plan_slug: '', plan_nombre: '' })); return }
    setForm(f => ({
      ...f,
      plan_slug: plan.slug,
      plan_nombre: plan.nombre,
      moneda: plan.moneda || f.moneda,
      lineas: [{ concepto: plan.nombre, monto: plan.precio }],
    }))
  }

  const setLinea = (i: number, campo: keyof RediviaLinea, valor: string) => {
    setForm(f => ({
      ...f,
      lineas: f.lineas.map((l, j) => j === i
        ? { ...l, [campo]: campo === 'monto' ? Number(valor || 0) : valor }
        : l),
    }))
  }

  const guardar = async () => {
    if (!form.cliente_id) { showToast('Elegí el cliente', 'error'); return }
    if (!/^[a-z0-9-]{2,}$/.test(form.tenant_slug)) {
      showToast('El slug del tenant va en minúsculas, números y guiones', 'error'); return
    }
    const lineas = form.lineas.filter(l => l.concepto.trim() && Number(l.monto) > 0)
    if (lineas.length === 0) { showToast('Agregá al menos una línea con concepto y monto', 'error'); return }

    const cuerpo = {
      cliente_id: Number(form.cliente_id),
      tenant_slug: form.tenant_slug,
      plan_slug: form.plan_slug || null,
      plan_nombre: form.plan_nombre || null,
      monto_mensual: r2(lineas.reduce((a, l) => a + Number(l.monto), 0)),
      moneda: form.moneda,
      lineas,
      dia_cobro: form.dia_cobro,
      notas: form.notas || null,
    }

    setGuardando(true)
    try {
      if (editId) await api.put(`/api/redivia/negocios/${editId}`, cuerpo)
      else await api.post('/api/redivia/negocios', cuerpo)
      showToast(editId ? 'Negocio actualizado' : 'Negocio dado de alta', 'success')
      setModal(null)
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'No se pudo guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const provisionar = async (n: RediviaNegocio) => {
    try {
      await api.post(`/api/redivia/negocios/${n.id}/provisionar`, {})
      showToast(`Redivia provisionó «${n.tenant_slug}»`, 'success')
      await cargar()
    } catch (e: any) {
      showToast(e?.message || 'Redivia no pudo provisionar', 'error')
    }
  }

  /* Reactivar no pide motivo: no hay nada que explicarle al cliente cuando se
     le devuelve el acceso. Cortar sí, y ese texto lo lee él. */
  const cambiarEstado = async (n: RediviaNegocio, estado: string, motivo?: string) => {
    setOcupado(n.id)
    try {
      await api.post(`/api/redivia/negocios/${n.id}/estado`, motivo ? { estado, motivo } : { estado })
      showToast(
        estado === 'activo' ? 'Acceso devuelto en el panel del cliente'
          : estado === 'suspendido' ? 'Panel cortado por impago'
          : 'Negocio dado de baja',
        'success',
      )
      setCorte(null)
      await cargar()
    } catch (e: any) {
      // El estado local no se movió: el error es del panel, no del ERP.
      showToast(e?.message || 'No se pudo cambiar el panel en Redivia', 'error')
    } finally {
      setOcupado(null)
    }
  }

  if (loading) return (
    <div className="redivia__cargando">
      <div className="auth-spinner" />Cargando negocios...
    </div>
  )

  return (
    <>
      <div className="page-actions">
        <button className="btn btn-primary" onClick={abrirAlta} id="btn-nuevo-negocio-redivia">
          <IconPlus size={16} /> Nuevo Negocio
        </button>
      </div>

      {negocios.length > 0 && kpis && (
        <KpiStrip>
          {/* Una KpiRow por moneda: sumar monedas distintas no significa nada. */}
          {Object.entries(kpis.mrr).map(([moneda, monto]) => (
            <KpiRow key={moneda} moneda={moneda}>
              <StatCard label="Ingreso mensual" value={formatCurrency(monto, moneda)} tone="info" />
            </KpiRow>
          ))}
          <KpiRow>
            <StatCard label="Activos" value={kpis.activos} tone="success" />
            <StatCard label="Suspendidos" value={kpis.suspendidos} tone={kpis.suspendidos > 0 ? 'warning' : 'default'} />
            <StatCard label="Cobros pendientes" value={kpis.cobros_pendientes} tone={kpis.cobros_pendientes > 0 ? 'warning' : 'default'} />
            {/* Los dos que no se ven en la cobranza y deciden una llamada. */}
            <StatCard label="Cerca del tope" value={kpis.cerca_del_tope}
              tone={kpis.cerca_del_tope > 0 ? 'warning' : 'default'} />
            <StatCard label="Plan chico" value={kpis.con_upsell}
              tone={kpis.con_upsell > 0 ? 'info' : 'default'} />
          </KpiRow>
        </KpiStrip>
      )}

      {/* Sin los datos de Redivia la pantalla muestra la cobranza igual, pero
          las columnas de uso van vacías: hay que decir por qué, o parecen ceros. */}
      {kpis && !kpis.uso_disponible && (
        <div className="redivia__aviso">
          <IconAlertTriangle size={14} />
          No se pudo leer el uso desde Redivia: el cupo, los usuarios y la actividad salen en blanco.
          La cobranza de abajo es del ERP y no depende de eso.
        </div>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por empresa, tenant o plan..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 180 }}>
          <option value="todos">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear"
            onClick={() => { setSearch(''); setFiltroEstado('todos') }}>Limpiar filtros</button>
        )}
      </div>

      {negocios.length === 0 ? (
        <div className="empty-state">
          <h3>Sin negocios</h3>
          <p>Dá de alta el primer negocio de Redivia para que su membresía se cobre desde acá.</p>
          <button className="btn btn-primary" onClick={abrirAlta}>Nuevo Negocio</button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Tenant</th>
                  <th>Plan</th>
                  <th>Clientes</th>
                  <th>Actividad 30d</th>
                  <th>Mensual</th>
                  <th>Próximo cobro</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginated.map(n => (
                  <tr key={n.id} className={n.estado === 'cancelado' ? 'row--dimmed' : undefined}>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{n.cliente_nombre || `Cliente ${n.cliente_id}`}</span>
                        {n.cliente_ruc && <span className="cell-stack__sub">RUC {n.cliente_ruc}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{n.tenant_slug}</span>
                        {!n.provisionado_at && <span className="cell-stack__sub">sin provisionar</span>}
                      </div>
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{n.plan_nombre || '—'}</span>
                        {/* Si el uso sugiere un tramo mayor que el contratado,
                            el plan le quedó chico: eso es una llamada, no un dato. */}
                        {hayUpsell(n) && (
                          <span className="badge badge-warning" title={`El uso sugiere el tramo ${n.uso?.tramo_sugerido}`}>
                            sugiere {n.uso?.tramo_sugerido}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {n.uso?.clientes_activos != null ? (
                        <div className="cell-stack">
                          <span className="cell-stack__title">
                            {n.uso.clientes_activos}
                            {n.uso.clientes_tope != null && <span className="cell-soft"> / {n.uso.clientes_tope}</span>}
                          </span>
                          {n.uso.clientes_pct != null && (
                            <span className={n.uso.clientes_pct >= 80 ? 'redivia__tope' : 'cell-stack__sub'}>
                              {n.uso.clientes_pct}% del cupo
                            </span>
                          )}
                        </div>
                      ) : <span className="cell-soft">—</span>}
                    </td>
                    <td>
                      {n.uso ? (
                        <div className="cell-stack">
                          <span className="cell-stack__title">{(n.uso.entregas_30d ?? 0).toLocaleString('es-PE')} entregas</span>
                          <span className="cell-stack__sub">
                            {(n.uso.mensajes_30d ?? 0).toLocaleString('es-PE')} mensajes · {n.uso.usuarios_activos ?? 0} usuarios
                          </span>
                        </div>
                      ) : <span className="cell-soft">—</span>}
                    </td>
                    <td>{formatCurrency(n.monto_mensual, n.moneda)}</td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{n.proximo_cobro ? formatDate(n.proximo_cobro) : '—'}</span>
                        <span className="cell-stack__sub">día {n.dia_cobro} de cada mes</span>
                      </div>
                    </td>
                    <td>
                      <div className="cell-stack">
                        <span className={`badge ${ESTADO_BADGE[n.estado] || ''}`}>{ESTADO_LABEL[n.estado] || n.estado}</span>
                        {/* El estado del ERP es lo contratado; el del panel es lo
                            que de verdad funciona. Cuando no coinciden, se avisa. */}
                        {n.uso && n.estado === 'activo' && (n.uso.suspendido === true || n.uso.activo === false) && (
                          <span className="cell-stack__sub redivia__tope">
                            {n.uso.suspendido ? 'panel cortado' : 'dado de baja'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="row-actions row-actions--nowrap">
                      {/* Sin correo de facturación el comprobante no se puede
                          enviar, y el botón de enviar fallaría sin explicar por qué. */}
                      {!n.contacto_email && (
                        <span className="redivia__sin-correo" title="El cliente no tiene contacto con correo: el comprobante no se puede enviar">
                          <IconMail size={14} />
                          <IconAlertTriangle size={11} />
                        </span>
                      )}
                      {!n.provisionado_at && (
                        <button className="btn btn-sm btn-secondary" onClick={() => void provisionar(n)}
                          title="Pedirle a Redivia que cree el tenant">
                          <IconZap size={14} />
                        </button>
                      )}
                      {n.uso && (
                        <button className="btn btn-sm btn-secondary" onClick={() => setDetalle(n)}
                          title="Ver el detalle operativo del panel">
                          <IconBarChart size={14} />
                        </button>
                      )}
                      <button className="btn btn-sm btn-secondary" onClick={() => abrirEdicion(n)} title="Editar">
                        <IconEdit size={14} />
                      </button>
                      {n.estado === 'activo' ? (
                        <button className="btn btn-sm btn-secondary" disabled={ocupado === n.id}
                          onClick={() => { setCorte({ n, estado: 'suspendido' }); setMotivoCorte('') }}
                          title="Cortar el acceso del cliente por impago">
                          <IconBan size={14} />
                        </button>
                      ) : n.estado === 'suspendido' ? (
                        <button className="btn btn-sm btn-secondary" disabled={ocupado === n.id}
                          onClick={() => void cambiarEstado(n, 'activo')}
                          title="Devolverle el acceso al cliente">
                          <IconCheck size={14} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} noun="negocios" />
        </>
      )}

      <Modal
        title={editId ? 'Editar negocio' : 'Nuevo negocio de Redivia'}
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={() => void guardar()} disabled={guardando}>
              {guardando ? 'Guardando...' : editId ? 'Guardar' : 'Dar de alta'}
            </button>
          </>
        }
      >
        <div>
          <div className="form-row">
          <div className="form-group">
            <label className="form-label">Cliente</label>
            <select className="form-input" value={form.cliente_id}
              onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value ? Number(e.target.value) : '' }))}
              disabled={!!editId}>
              <option value="">Elegir cliente...</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Tenant en Redivia</label>
            <input className="form-input" placeholder="libreovo" value={form.tenant_slug}
              onChange={e => setForm(f => ({ ...f, tenant_slug: e.target.value.toLowerCase().trim() }))}
              disabled={!!editId} />
            <span className="form-hint">El slug con el que Redivia identifica el panel. No se cambia después.</span>
          </div>
          </div>

          <div className="form-row">
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-input" value={form.plan_slug} onChange={e => aplicarPlan(e.target.value)}>
              <option value="">{catalogo?.disponible ? 'Elegir plan del catálogo...' : 'Catálogo no disponible'}</option>
              {catalogo?.planes.map(p => (
                <option key={p.slug} value={p.slug}>{p.nombre} — {p.moneda} {p.precio.toFixed(2)}</option>
              ))}
            </select>
            {catalogo && !catalogo.disponible && (
              <span className="form-hint form-hint--warning">
                No se pudo leer el catálogo de Redivia ({catalogo.motivo}). Cargá el concepto y el monto a mano.
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Día de cobro</label>
            <input className="form-input" type="number" min={1} max={28} value={form.dia_cobro}
              onChange={e => setForm(f => ({ ...f, dia_cobro: Number(e.target.value) }))} />
            <span className="form-hint">1 al 28, para que el cobro exista también en febrero.</span>
          </div>
          </div>

          <div className="form-group">
            <label className="form-label">Desglose (IGV incluido)</label>
            {form.lineas.map((l, i) => (
              <div key={i} className="redivia__linea">
                <input className="form-input" placeholder="Concepto (Plan Negocio, WhatsApp PRO...)"
                  value={l.concepto} onChange={e => setLinea(i, 'concepto', e.target.value)} />
                <input className="form-input" type="number" step="0.01" min="0" placeholder="0.00"
                  value={l.monto || ''} onChange={e => setLinea(i, 'monto', e.target.value)} />
                {form.lineas.length > 1 && (
                  <button className="btn btn-sm btn-secondary" type="button"
                    onClick={() => setForm(f => ({ ...f, lineas: f.lineas.filter((_, j) => j !== i) }))}>
                    <IconTrash size={14} />
                  </button>
                )}
              </div>
            ))}
            <button className="btn btn-sm btn-secondary" type="button"
              onClick={() => setForm(f => ({ ...f, lineas: [...f.lineas, { concepto: '', monto: 0 }] }))}>
              <IconPlus size={14} /> Agregar línea
            </button>
            <span className="form-hint">
              Total mensual: <strong>{formatCurrency(montoForm, form.moneda)}</strong> — es la suma de las líneas,
              y es el desglose que se imprime en el comprobante.
            </span>
          </div>

          <div className="form-group">
            <label className="form-label">Moneda</label>
            <select className="form-input" value={form.moneda}
              onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
              <option value="PEN">PEN</option>
              <option value="USD">USD</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-input" rows={2} value={form.notas}
              onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* El corte se confirma con el texto que el cliente va a leer. No es un
          campo interno: Redivia se lo imprime en su pantalla de suspensión. */}
      <Modal
        title={corte?.estado === 'cancelado' ? 'Dar de baja el negocio' : 'Cortar el acceso por impago'}
        isOpen={corte !== null}
        onClose={() => setCorte(null)}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCorte(null)}>Cancelar</button>
            <button className="btn btn-primary" disabled={ocupado !== null}
              onClick={() => corte && void cambiarEstado(corte.n, corte.estado, motivoCorte.trim() || undefined)}>
              {ocupado !== null ? 'Aplicando...' : corte?.estado === 'cancelado' ? 'Dar de baja' : 'Cortar acceso'}
            </button>
          </>
        }
      >
        <div>
          <div className="form-group">
            <label className="form-label">Motivo</label>
            <input className="form-input" placeholder="Cobro 2026-11 vencido" value={motivoCorte}
              onChange={e => setMotivoCorte(e.target.value)} maxLength={200} />
            <span className="form-hint form-hint--warning">
              Este texto se le muestra al cliente en su pantalla. Nada de notas internas.
            </span>
          </div>
          <div className="form-group">
            <span className="form-hint">
              {corte?.estado === 'cancelado'
                ? 'La baja cierra el ingreso y además corta la sesión abierta. El pago no la deshace: para volver hay que reactivarlo desde acá.'
                : 'El cliente sigue entrando y ve la página de pago; el corte toma efecto en su próxima acción. Cuando el pago se registre, el acceso vuelve solo.'}
            </span>
          </div>
        </div>
      </Modal>

      {/* Detalle operativo: todo esto vive en Redivia y se lee en vivo. El ERP
          no lo guarda — es estado que cambia solo y cuya fuente es el panel. */}
      <Modal
        title={detalle ? `${detalle.cliente_nombre || detalle.tenant_slug} — panel` : 'Panel'}
        isOpen={detalle !== null}
        onClose={() => setDetalle(null)}
        footer={<button className="btn btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>}
      >
        {detalle?.uso && (
          <div className="redivia__detalle">
            <div>
              <span className="redivia__detalle-label">Plan en el panel</span>
              <span className="redivia__detalle-valor">{detalle.uso.plan_remoto || '—'}</span>
            </div>
            <div>
              <span className="redivia__detalle-label">Tramo que sugiere el uso</span>
              <span className="redivia__detalle-valor">
                {detalle.uso.tramo_sugerido || '—'}
                {hayUpsell(detalle) && <span className="badge badge-warning">le quedó chico</span>}
              </span>
            </div>
            <div>
              <span className="redivia__detalle-label">Clientes</span>
              <span className="redivia__detalle-valor">
                {detalle.uso.clientes_activos ?? '—'}
                {detalle.uso.clientes_tope != null && ` de ${detalle.uso.clientes_tope}`}
                {detalle.uso.clientes_pct != null && (
                  <span className={detalle.uso.clientes_pct >= 80 ? 'redivia__tope' : 'cell-soft'}>
                    {detalle.uso.clientes_pct}%
                  </span>
                )}
              </span>
            </div>
            <div>
              <span className="redivia__detalle-label">Usuarios del negocio</span>
              <span className="redivia__detalle-valor">{detalle.uso.usuarios_activos ?? '—'}</span>
            </div>
            <div>
              <span className="redivia__detalle-label">Entregas (30 días)</span>
              <span className="redivia__detalle-valor">{(detalle.uso.entregas_30d ?? 0).toLocaleString('es-PE')}</span>
            </div>
            <div>
              <span className="redivia__detalle-label">Mensajes (30 días)</span>
              <span className="redivia__detalle-valor">{(detalle.uso.mensajes_30d ?? 0).toLocaleString('es-PE')}</span>
            </div>
            <div className="redivia__detalle-ancho">
              <span className="redivia__detalle-label">Módulos encendidos</span>
              <span className="redivia__detalle-valor">
                {detalle.uso.modulos.length
                  ? detalle.uso.modulos.map(m => (
                      <span key={m} className="badge badge-categoria">{MODULO_LABEL[m] || m}</span>
                    ))
                  : '—'}
              </span>
            </div>
            <div>
              <span className="redivia__detalle-label">Acceso del cliente</span>
              <span className="redivia__detalle-valor">
                {detalle.uso.suspendido
                  ? <span className="badge badge-warning">cortado por impago</span>
                  : detalle.uso.activo === false
                    ? <span className="badge badge-rechazada">dado de baja</span>
                    : <span className="badge badge-aceptada">con acceso</span>}
              </span>
            </div>
            {detalle.uso.suspendido && (
              <div className="redivia__detalle-ancho">
                <span className="redivia__detalle-label">Motivo que ve el cliente</span>
                <span className="redivia__detalle-valor">{detalle.uso.suspendido_motivo || '—'}</span>
              </div>
            )}
            <div>
              <span className="redivia__detalle-label">Cuenta de IA</span>
              <span className="redivia__detalle-valor">{detalle.uso.ia_cuenta_id || '—'}</span>
            </div>
            <div className="redivia__detalle-ancho">
              <span className="form-hint">
                Estos datos se leen en vivo desde Redivia y no se guardan en el ERP: la fuente es el
                panel. La cuenta de IA es la del gateway, por donde pasa el consumo del wrapper.
              </span>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
